<template>
  <div class="page-container">
    <div class="page-title-container d-flex align-items-center justify-content-between flex-wrap">
      <h1 class="page-title mb-0">Resumen de descuentos y adicionales</h1>
      <div class="d-flex align-items-center mt-2 mt-md-0">
        <button type="button" class="button button-light button-sm mr-2" @click="loadData">
          <i class="ri-refresh-line mr-1"></i>Recargar
        </button>
        <button type="button" class="button button-primary button-sm" @click="downloadExcel" :disabled="downloading">
          <i class="ri-file-excel-line mr-1"></i>
          {{ downloading ? 'Descargando...' : 'Descargar Excel' }}
        </button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="d-flex flex-wrap align-items-center mb-3 mt-3">
      <div class="mr-3 mb-2">
        <label class="label-filter mb-1">Mes</label>
        <FormSelect
          v-model="filters.month"
          :items="monthOptions"
          style="min-width: 140px"
          @input="loadData"
        />
      </div>
      <div class="mr-3 mb-2">
        <label class="label-filter mb-1">Año</label>
        <FormSelect
          v-model="filters.year"
          :items="yearOptions"
          style="min-width: 100px"
          @input="loadData"
        />
      </div>
      <div class="mr-3 mb-2" style="min-width: 200px">
        <label class="label-filter mb-1">Empresa</label>
        <SearchableDropdown
          :options="companyOptions"
          :oldValue="filters.companyId"
          placeholder="Todas"
          @selected="onCompanySelected"
        />
      </div>
      <div class="mr-3 mb-2" style="min-width: 180px">
        <label class="label-filter mb-1">Área</label>
        <SearchableDropdown
          :options="areaOptions"
          :oldValue="filters.areaId"
          placeholder="Todas"
          @selected="onAreaSelected"
        />
      </div>
    </div>

    <!-- Tabla -->
    <div class="table-container">
      <div class="table-responsive">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th>EMPLEADO</th>
              <th>DNI</th>
              <th>EMPRESA</th>
              <th>ÁREA</th>
              <th class="text-right">TOTAL DESCUENTOS</th>
              <th class="text-right">TOTAL ADICIONALES</th>
              <th class="text-right">ADIC. REMUNERATIVOS</th>
              <th class="text-right">ADIC. NO REMUNERATIVOS</th>
              <th class="text-right">NETO</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="rows.length === 0">
              <td colspan="9" class="text-center text-muted py-4">No hay datos para el período seleccionado.</td>
            </tr>
            <tr v-for="(row, idx) in rows" :key="idx">
              <td>{{ row.employeeName }}</td>
              <td>{{ row.employeeIdentifier }}</td>
              <td>{{ row.companyName }}</td>
              <td>{{ row.areaName }}</td>
              <td class="text-right">
                <span class="text-danger font-weight-medium">{{ formatAmount(row.totalDiscounts) }}</span>
              </td>
              <td class="text-right">
                <span class="text-success font-weight-medium">{{ formatAmount(row.totalAdditions) }}</span>
              </td>
              <td class="text-right">{{ formatAmount(row.additionsRemunerative) }}</td>
              <td class="text-right">{{ formatAmount(row.additionsNonRemunerative) }}</td>
              <td class="text-right">
                <span :class="row.netAmount >= 0 ? 'text-primary' : 'text-danger'" class="font-weight-bold">
                  {{ formatAmount(row.netAmount) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="table-footer d-flex justify-content-end align-items-center">
        <p v-if="rows.length > 0" class="m-0 px-3 text-muted small">{{ rows.length }} empleado(s)</p>
      </div>
    </div>
  </div>
</template>

<script>
import { Alert, FormSelect, SearchableDropdown } from 'wize-admin'
import { PayrollMovementService, CompanyService, AreaService } from '@/core/services'

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

export default {
  name: 'PayrollMovementSummary',
  components: { FormSelect, SearchableDropdown },

  data() {
    const now = new Date()
    return {
      rows:           [],
      companyOptions: [],
      areaOptions:    [],
      downloading:    false,
      monthOptions: MONTH_NAMES.map((m, i) => ({ key: i + 1, label: m })),
      filters: {
        month:     now.getMonth() + 1,
        year:      now.getFullYear(),
        companyId: null,
        areaId:    null,
      },
    }
  },

  computed: {
    yearOptions() {
      const current = new Date().getFullYear()
      return Array.from({ length: 5 }, (_, i) => ({ key: current - 2 + i, label: String(current - 2 + i) }))
    },
  },

  methods: {
    buildPayload() {
      const p = { periodYear: this.filters.year, periodMonth: this.filters.month }
      if (this.filters.companyId) p.companyId = this.filters.companyId
      if (this.filters.areaId)    p.areaId    = this.filters.areaId
      return p
    },

    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const resp = await PayrollMovementService.getSummary(this.buildPayload())
        this.rows = resp.payload || []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar el resumen')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },

    async downloadExcel() {
      try {
        this.downloading = true
        const resp = await PayrollMovementService.downloadSummaryXlsx(this.buildPayload())
        const url  = URL.createObjectURL(new Blob([resp.data]))
        const a    = document.createElement('a')
        a.href     = url
        a.download = `resumen-movimientos-${this.filters.year}-${String(this.filters.month).padStart(2, '0')}.xlsx`
        a.click()
        URL.revokeObjectURL(url)
      } catch (e) {
        Alert.error('Error al descargar el Excel')
      } finally {
        this.downloading = false
      }
    },

    onCompanySelected(id) { this.filters.companyId = id; this.loadData() },
    onAreaSelected(id)    { this.filters.areaId    = id; this.loadData() },

    formatAmount(value) {
      if (value == null) return 'S/ 0.00'
      return 'S/ ' + Number(value).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
  },

  async created() {
    try {
      const [companiesResp, areasResp] = await Promise.all([
        CompanyService.listAll(),
        AreaService.listAll(),
      ])
      this.companyOptions = (companiesResp.payload || []).map(c => ({ key: c.id, label: c.businessName || c.name }))
      this.areaOptions    = (areasResp.payload    || []).map(a => ({ key: a.id, label: a.name || a.description }))
    } catch (e) {
      console.error('Error al cargar catálogos', e)
    }
    this.loadData()
  },
}
</script>

<style lang="stylus" scoped></style>
